<template>
  <v-main>
    <v-container fluid>
      <!-- Campaign Info & Stats -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn fab small color="primary" @click="$router.back()">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <span class="ml-3 font-weight-bold">Campaign Logs</span>
            </v-card-title>
            <v-card-text v-if="campaign">
              <v-row>
                <v-col md="3">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h4 font-weight-bold primary--text">
                      {{ campaign.name }}
                    </div>
                    <div class="text-caption grey--text">Campaign Name</div>
                  </v-card>
                </v-col>
                <v-col md="2">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h4 font-weight-bold">
                      {{ stats.total_sent || 0 }}
                    </div>
                    <div class="text-caption grey--text">Total Sent</div>
                  </v-card>
                </v-col>
                <v-col md="2">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h4 font-weight-bold success--text">
                      {{ stats.total_delivered || 0 }}
                    </div>
                    <div class="text-caption grey--text">Delivered</div>
                  </v-card>
                </v-col>
                <v-col md="2">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h4 font-weight-bold error--text">
                      {{ stats.total_failed || 0 }}
                    </div>
                    <div class="text-caption grey--text">Failed</div>
                  </v-card>
                </v-col>
                <v-col md="3">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h4 font-weight-bold">
                      {{ stats.execution_count || 0 }}
                    </div>
                    <div class="text-caption grey--text">Executions</div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Channel-wise Stats -->
              <v-row class="mt-4" v-if="channelStats">
                <v-col md="4" v-for="(channelData, channel) in channelStats" :key="channel">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center mb-2">
                      <v-icon :color="getChannelColor(channel)" class="mr-2">
                        {{ getChannelIcon(channel) }}
                      </v-icon>
                      <strong>{{ channel | capitalize }}</strong>
                    </div>
                    <div class="d-flex justify-space-between">
                      <span class="success--text">Sent: {{ channelData.sent || 0 }}</span>
                      <span class="error--text">Failed: {{ channelData.failed || 0 }}</span>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row class="mt-4">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-row>
                <v-col md="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.channel"
                    :items="channelOptions"
                    clearable
                    label="Filter by Channel"
                    @input="getData"
                    prepend-inner-icon="mdi-filter"
                  ></v-autocomplete>
                </v-col>
                <v-col md="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    clearable
                    label="Filter by Status"
                    @input="getData"
                    prepend-inner-icon="mdi-filter"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Logs Table -->
      <v-row class="mt-4">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="400" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left" style="min-width: 60px">Sr No</th>
                      <th class="text-left" style="min-width: 200px">User</th>
                      <th class="text-left" style="min-width: 120px">Phone</th>
                      <th class="text-left" style="min-width: 100px">Channel</th>
                      <th class="text-left" style="min-width: 100px">Status</th>
                      <th class="text-left" style="min-width: 150px">Sent At</th>
                      <th class="text-left" style="min-width: 200px">Error Message</th>
                    </tr>
                  </thead>
                  <tbody v-if="logs.length != 0">
                    <tr
                      v-for="(log, i) in logs"
                      :key="`log_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                      }"
                    >
                      <td>{{ serialNoStarting + i + 1 }}</td>
                      <td>
                        <div v-if="log.user">
                          {{ log.user.first_name }} {{ log.user.last_name }}
                          <div class="text-caption grey--text">{{ log.user.email }}</div>
                        </div>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td>{{ log.user?.phone || '-' }}</td>
                      <td>
                        <v-chip
                          small
                          :color="getChannelColor(log.channel)"
                          text-color="white"
                        >
                          <v-icon small left>{{ getChannelIcon(log.channel) }}</v-icon>
                          {{ log.channel | capitalize }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getStatusColor(log.status)"
                          text-color="white"
                        >
                          {{ log.status | capitalize }}
                        </v-chip>
                      </td>
                      <td>
                        {{ log.sent_at | niceDate }}
                      </td>
                      <td>
                        <span v-if="log.error_message" class="error--text text-caption">
                          {{ log.error_message | truncate(50) }}
                        </span>
                        <span v-else class="grey--text">-</span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7" class="text-center">No logs found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "MarketingCampaignLogsPage",
  data() {
    return {
      campaignId: null,
      campaign: null,
      stats: {},
      channelStats: null,
      logs: [],
      filter: {
        channel: "",
        status: "",
      },
      page: 1,
      serialNoStarting: 0,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      channelOptions: [
        { text: "Push Notification", value: "push" },
        { text: "SMS", value: "sms" },
        { text: "WhatsApp", value: "whatsapp" },
      ],
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Sent", value: "sent" },
        { text: "Delivered", value: "delivered" },
        { text: "Failed", value: "failed" },
      ],
    };
  },
  filters: {
    truncate(value, length) {
      if (!value) return "";
      if (value.length <= length) return value;
      return value.substring(0, length) + "...";
    },
    capitalize(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  watch: {
    page: "getData",
  },
  async mounted() {
    this.campaignId = this.$route.params.campaignId;
    if (!this.campaignId) {
      this.$router.back();
      return;
    }
    await this.getStats();
    await this.getData();
  },
  methods: {
    async getStats() {
      try {
        const response = await axios.get(`marketing-campaigns/${this.campaignId}/stats`);
        this.campaign = response.data.data.campaign;
        this.stats = response.data.data.campaign;
        this.channelStats = response.data.data.by_channel;
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    },

    async getData() {
      this.isLoading = true;
      try {
        let query = new URLSearchParams({
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          channel: this.filter.channel || "",
          status: this.filter.status || "",
        }).toString();

        const response = await axios.get(`marketing-campaigns/${this.campaignId}/logs?${query}`);
        this.logs = response.data.data;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error("Failed to fetch logs:", error);
      }
      this.isLoading = false;
    },

    getChannelColor(channel) {
      const colors = {
        push: "purple",
        sms: "indigo",
        whatsapp: "green",
      };
      return colors[channel] || "grey";
    },

    getChannelIcon(channel) {
      const icons = {
        push: "mdi-bell",
        sms: "mdi-message-text",
        whatsapp: "mdi-whatsapp",
      };
      return icons[channel] || "mdi-send";
    },

    getStatusColor(status) {
      const colors = {
        pending: "grey",
        sent: "blue",
        delivered: "green",
        failed: "red",
      };
      return colors[status] || "grey";
    },
  },
};
</script>
